import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SELECT_CLS } from "@/lib/network";

function CustomerSearch({ odpId, onPick, busy }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/network/customers/search", { params: { q, odp_id: odpId } })
        .then((r) => setRes(r.data)).catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)));
    }, 300);
    return () => clearTimeout(t);
  }, [q, odpId]);
  return (
    <div className="space-y-2">
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari ID, nama, atau alamat pelanggan…" data-testid="place-search-input" />
      {res?.mapping_empty && (
        <p className="rounded bg-[#FFFBEB] p-2 text-xs text-[#B45309]" data-testid="place-mapping-empty">
          Wilayah ODP ini belum dipetakan ke wilayah pelanggan. Admin perlu mengisi Pemetaan Wilayah di Master ODC.
        </p>
      )}
      <div className="max-h-72 divide-y overflow-y-auto rounded border border-[#E5E7EB]" data-testid="place-search-results">
        {(res?.items || []).map((c) => (
          <div key={c.customer_id} className="flex items-center justify-between gap-2 p-2 text-sm" data-testid={`place-result-${c.customer_id}`}>
            <div className="min-w-0">
              <div className="font-semibold"><span className="font-mono-tab">{c.customer_id}</span> · {c.nama_customer}</div>
              <div className="truncate text-xs text-[#6B7280]">{c.alamat || "-"} · {c.nama_wilayah || c.wilayah_id || "-"}</div>
              {c.current && <div className="text-xs text-[#B45309]">Saat ini: {c.current.odp_kode} port {c.current.port}</div>}
            </div>
            <Button size="sm" disabled={busy} variant={c.current ? "outline" : "default"} onClick={() => onPick(c)}
                    data-testid={`place-pick-${c.customer_id}`}>{c.current ? "Pindahkan ke sini" : "Tempatkan"}</Button>
          </div>
        ))}
        {res && !res.items.length && <p className="p-3 text-center text-xs text-[#6B7280]">Tidak ada pelanggan.</p>}
      </div>
    </div>
  );
}

function MoveTarget({ me, onPick, busy, excludeOdpId, excludePort }) {
  const [odps, setOdps] = useState([]);
  const [odpId, setOdpId] = useState("");
  const [ports, setPorts] = useState([]);
  useEffect(() => {
    api.get("/network/odp", { params: { status: "AKTIF" } }).then((r) =>
      setOdps(r.data.filter((o) => me?.is_admin || (me?.wilayah_ids || []).includes(o.wilayah_id))));
  }, [me]);
  useEffect(() => {
    if (!odpId) return setPorts([]);
    api.get(`/network/odp/${odpId}`).then((r) => setPorts(r.data.ports.filter((p) => !p.customer_id && !(odpId === excludeOdpId && p.port === excludePort))));
  }, [odpId, excludeOdpId, excludePort]);
  return (
    <div className="space-y-2">
      <Label>ODP tujuan</Label>
      <select className={`${SELECT_CLS} w-full`} value={odpId} onChange={(e) => setOdpId(e.target.value)} data-testid="move-target-odp">
        <option value="">— Pilih ODP —</option>
        {odps.map((o) => <option key={o.id} value={o.id}>{o.kode} — {o.nama} ({o.port_kosong} kosong)</option>)}
      </select>
      {odpId && (
        <div className="flex flex-wrap gap-1.5" data-testid="move-target-ports">
          {ports.map((p) => (
            <Button key={p.port} size="sm" variant="outline" disabled={busy} onClick={() => onPick(odpId, p.port)}
                    data-testid={`move-port-${p.port}`}>Port {p.port}</Button>
          ))}
          {!ports.length && <span className="text-xs text-[#6B7280]">Tidak ada port kosong.</span>}
        </div>
      )}
    </div>
  );
}

export function PlaceDialog({ open, mode, odp, port, customer, me, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const send = async (url, body, msg) => {
    setBusy(true);
    try {
      await api.post(url, body);
      toast.success(msg);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };
  const pickCustomer = (c) => send(c.current ? "/network/placements/move" : "/network/placements",
    { customer_id: c.customer_id, odp_id: odp.id, port }, `${c.customer_id} → ${odp.kode} port ${port}`);
  const pickTarget = (odpId, p) => send("/network/placements/move", { customer_id: customer.customer_id, odp_id: odpId, port: p },
    `${customer.customer_id} dipindahkan ke port ${p}`);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="place-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "move" ? `Pindahkan ${customer?.customer_id}` : `Isi ${odp?.kode} · Port ${port}`}</DialogTitle>
          <DialogDescription>
            {mode === "move" ? `Dari ${odp?.kode} port ${port}. Penempatan lama ditutup dan tercatat di riwayat.`
              : "Pilih pelanggan dari master pelanggan. Data pelanggan tidak diubah."}
          </DialogDescription>
        </DialogHeader>
        {mode === "move"
          ? <MoveTarget me={me} busy={busy} onPick={pickTarget} excludeOdpId={odp?.id} excludePort={port} />
          : <CustomerSearch odpId={odp?.id} busy={busy} onPick={pickCustomer} />}
      </DialogContent>
    </Dialog>
  );
}
